import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Select } from "@shopify/polaris";
import { CalendarIcon, PlusIcon } from "@shopify/polaris-icons";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import useIsPhone from "features/files/use-is-phone";

export interface LeaveCalendarToolbarProps {
  year: number;
  monthIndex: number;
  viewMode: "month" | "year";
  onViewModeChange: (mode: "month" | "year") => void;
  onYearChange: (y: number) => void;
  onMonthChange: (m: number) => void;
  onJumpToday: () => void;
  onAddLeave: () => void;
  /** False while the calendar's data is loading, or when there is no day left to add a leave on. */
  canAddLeave: boolean;
}

// A phone toolbar button is the base (size and shape) plus a colour set plus a width (side padding,
// or a fixed width for an arrow). Each property comes from exactly one of the three: two utilities
// for the same property in one className are decided by their order in the stylesheet, not by the
// order in the string.
const phoneButtonBaseCls =
  "inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg border text-sm font-medium disabled:opacity-40";
const lightColoursCls = "border-slate-200 bg-white text-slate-700";
const darkColoursCls = "border-slate-800 bg-slate-800 text-white";

/** «Today» and «Filters». */
const phoneLightButtonCls = `${phoneButtonBaseCls} ${lightColoursCls} px-3`;
/** Previous and next month: a 44 px square. */
const phoneArrowButtonCls = `${phoneButtonBaseCls} ${lightColoursCls} w-11`;
/** The one main action, «add leave». */
const phonePrimaryButtonCls = `${phoneButtonBaseCls} ${darkColoursCls} px-3`;

export function LeaveCalendarToolbar({
  year,
  monthIndex,
  viewMode,
  onViewModeChange,
  onYearChange,
  onMonthChange,
  onJumpToday,
  onAddLeave,
  canAddLeave,
}: LeaveCalendarToolbarProps) {
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);

  const currentYear = new Date().getFullYear();
  const firstYear = currentYear - 1;
  const lastYear = currentYear + 2;

  const yearOptions = useMemo(() => {
    const opts: { label: string; value: string }[] = [];
    for (let y = firstYear; y <= lastYear; y += 1) {
      opts.push({ label: String(y), value: String(y) });
    }
    return opts;
  }, [firstYear, lastYear]);

  const lang = (i18n as any).language as string | undefined;

  const monthOptions = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(lang === "lt" ? "lt-LT" : "en-US", {
      month: "long",
    });
    return Array.from({ length: 12 }, (_, m) => ({
      label: fmt.format(new Date(2000, m, 1)),
      value: String(m),
    }));
  }, [lang]);

  const viewSelect = (
    <Select
      label={t("leave_calendar_view")}
      options={[
        { label: t("leave_calendar_monthly", "Monthly"), value: "month" },
        { label: t("leave_calendar_yearly", "Yearly"), value: "year" },
      ]}
      value={viewMode}
      onChange={(v) => onViewModeChange(v as "month" | "year")}
    />
  );
  const yearSelect = (
    <Select
      label={t("leave_calendar_year")}
      options={yearOptions}
      value={String(year)}
      onChange={(v) => onYearChange(Number(v))}
    />
  );
  const monthSelect = (
    <Select
      label={t("leave_calendar_month")}
      options={monthOptions}
      value={String(monthIndex)}
      onChange={(v) => onMonthChange(Number(v))}
    />
  );

  if (!isPhone) {
    return (
      <div className="mb-4 flex flex-col gap-4 rounded-2xl border border-slate-100 bg-white/80 p-4 shadow-sm backdrop-blur-md lg:flex-row lg:items-end lg:justify-between focus-within:ring-4 focus-within:ring-slate-100 focus-within:border-slate-200/50 transition-all duration-300">
        <div className="flex flex-wrap items-end gap-3.5 flex-1">
          <div className="min-w-[140px]">{viewSelect}</div>
          <div className="min-w-[100px]">{yearSelect}</div>
          <div className="min-w-[140px]">{monthSelect}</div>
        </div>
        <div className="flex shrink-0 gap-2 pb-0.5">
          <Button icon={<CalendarIcon />} onClick={onJumpToday}>
            {t("leave_calendar_today")}
          </Button>
          <Button
            variant="primary"
            icon={<PlusIcon />}
            disabled={!canAddLeave}
            onClick={onAddLeave}
          >
            {t("leave_calendar_add_leave")}
          </Button>
        </div>
      </div>
    );
  }

  // The month (the year, in the yearly view) one step before or after the one shown.
  const neighbour = (direction: -1 | 1) => {
    if (viewMode === "year") return { year: year + direction, monthIndex };
    const target = new Date(year, monthIndex + direction, 1);
    return { year: target.getFullYear(), monthIndex: target.getMonth() };
  };
  // The year list ends at both sides, so do the arrows.
  const canStep = (direction: -1 | 1) => {
    const { year: targetYear } = neighbour(direction);
    return targetYear >= firstYear && targetYear <= lastYear;
  };
  const step = (direction: -1 | 1) => {
    const target = neighbour(direction);
    onYearChange(target.year);
    onMonthChange(target.monthIndex);
  };
  const title =
    viewMode === "year"
      ? String(year)
      : `${monthOptions[monthIndex].label} ${year}`;

  return (
    <div className="mb-2 rounded-2xl border border-slate-100 bg-white/80 p-2 shadow-sm">
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={t("leave_calendar_previous")}
          disabled={!canStep(-1)}
          onClick={() => step(-1)}
          className={phoneArrowButtonCls}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1 truncate text-center text-base font-semibold capitalize text-slate-800">
          {title}
        </div>
        <button
          type="button"
          aria-label={t("leave_calendar_next")}
          disabled={!canStep(1)}
          onClick={() => step(1)}
          className={phoneArrowButtonCls}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
        <button type="button" onClick={onJumpToday} className={phoneLightButtonCls}>
          {t("leave_calendar_today")}
        </button>
      </div>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          aria-expanded={areFiltersOpen}
          onClick={() => setAreFiltersOpen(!areFiltersOpen)}
          className={`${phoneLightButtonCls} flex-1`}
        >
          {t("filters")}
          <ChevronDown
            className={areFiltersOpen ? "h-4 w-4 rotate-180" : "h-4 w-4"}
          />
        </button>
        <button
          type="button"
          disabled={!canAddLeave}
          onClick={onAddLeave}
          className={`${phonePrimaryButtonCls} flex-1`}
        >
          {t("leave_calendar_add_leave")}
        </button>
      </div>
      {/* A change in any of the selects reaches the panel and closes it, so the grid gets its height back. */}
      {areFiltersOpen && (
        <div
          className="mt-2 grid grid-cols-2 gap-2"
          onChange={() => setAreFiltersOpen(false)}
        >
          {viewSelect}
          {yearSelect}
          <div className="col-span-2">{monthSelect}</div>
        </div>
      )}
    </div>
  );
}
